import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Pencil } from "lucide-react";

const FIELDS = [
  ["line1", "House / Street / Village *"], ["line2", "Area / Landmark"], ["post_office", "Post Office"],
  ["police_station", "Police Station"], ["district", "District *"], ["state", "State *"], ["pincode", "PIN code *"],
];

export const addressLine = (a = {}) => [a.line1, a.line2, a.post_office && `PO: ${a.post_office}`, a.police_station && `PS: ${a.police_station}`,
  a.district, a.state, a.pincode && `PIN ${a.pincode}`].filter(Boolean).join(", ");

export const AddressCard = ({ client, path = "/address/me", title = "My Full Address", plain = false }) => {
  const [a, setA] = useState(null);
  const [f, setF] = useState({});
  const [edit, setEdit] = useState(false);
  const cRef = useRef(client);
  cRef.current = client;
  const load = useCallback(() => {
    cRef.current.get(path).then((r) => { setA(r.data.address || {}); setF({ state: "West Bengal", ...(r.data.address || {}) }); setEdit(!r.data.address?.line1); }).catch(() => setA({}));
  }, [path]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { const r = await cRef.current.put(path, f); setA(r.data.address); setEdit(false); toast.success("Address saved"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  if (a === null) return null;
  const body = (
    <div data-testid="address-card">
      <div className="flex items-center justify-between mb-2">
        <p className="eyebrow text-gold flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {title}</p>
        {!edit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="address-edit-btn" className="h-7 text-xs gap-1 border-gold/40 text-gold"><Pencil className="w-3 h-3" /> {a.line1 ? "Edit" : "Add"}</Button>}
      </div>
      {!edit ? (
        <p className="text-sm text-white" data-testid="address-text">{a.line1 ? addressLine(a) : "No address added yet."}</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          {FIELDS.map(([k, label]) => (
            <Input key={k} data-testid={`address-${k}`} value={f[k] || ""} placeholder={label} inputMode={k === "pincode" ? "numeric" : undefined} maxLength={k === "pincode" ? 6 : 200}
              onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9 bg-card/60" />
          ))}
          <div className="flex gap-2 sm:col-span-2">
            <Button onClick={save} data-testid="address-save-btn" className="h-9 bg-gold text-[#0a2e22] hover:bg-gold/90">Save address</Button>
            {a.line1 && <Button variant="ghost" onClick={() => { setF(a); setEdit(false); }} className="h-9" data-testid="address-cancel-btn">Cancel</Button>}
          </div>
        </div>
      )}
    </div>
  );
  return plain ? body : <Card className="p-4 bg-card/40 border-gold/30">{body}</Card>;
};
